/**
 * CRUD 列表管理 Hook
 *
 * 封装 ProTable 列表页常见的完整 CRUD 逻辑：
 * 数据请求、搜索/筛选、删除确认、页面导航等。
 * 比 useTableData 更完整，适用于标准的列表管理页面。
 */

import { useCallback, useRef } from 'react';
import type {
  ApiResponse,
  PaginatedData,
  ProTableResponse,
} from '../types/api';
import { isApiSuccess } from '../utils/api-helpers';
import { normalizeError } from './shared-async';
import { fetchPaginatedData, type TableRequestParams } from './shared-request';
import { useLatestRef } from './useLatestRef';

/**
 * CRUD 列表配置
 */
export interface UseCrudListOptions<T, P = Record<string, unknown>> {
  /** 列表数据请求 API */
  fetchApi: (params: P) => Promise<ApiResponse<PaginatedData<T>>>;
  /** 删除单条记录 API */
  deleteApi?: (id: string | number) => Promise<ApiResponse<unknown>>;
  /** 批量删除 API */
  batchDeleteApi?: (ids: (string | number)[]) => Promise<ApiResponse<unknown>>;
  /** 成功状态码，默认 '200' */
  successCode?: string | number;
  /** 请求前的参数转换 */
  transformParams?: (params: TableRequestParams) => P;
  /** 响应后的数据转换 */
  transformData?: (list: T[]) => T[];
  /** 页面路径前缀（用于导航到创建/编辑/详情页） */
  basePath?: string;
  /** 导航函数 */
  navigate?: (path: string) => void;
  /** 消息展示函数 */
  showMessage?: (
    content: string,
    type: 'success' | 'error' | 'warning',
  ) => void;
  /** 确认弹窗函数 */
  showConfirm?: (options: {
    title: string;
    content: string;
    onOk: () => Promise<void>;
  }) => void;
  /** 请求失败回调 */
  onError?: (error: Error) => void;
  /** 自定义文案（用于国际化） */
  messages?: Partial<CrudListMessages>;
}

/** CRUD 列表文案配置 */
interface CrudListMessages {
  deleteSuccess: string;
  deleteFailed: string;
  batchDeleteSuccess: (count: number) => string;
  batchDeleteFailed: string;
  confirmDeleteTitle: string;
  confirmDeleteContent: (name?: string) => string;
  confirmBatchDeleteTitle: string;
  confirmBatchDeleteContent: (count: number) => string;
}

const DEFAULT_CRUD_MESSAGES: CrudListMessages = {
  deleteSuccess: 'Deleted successfully',
  deleteFailed: 'Delete failed',
  batchDeleteSuccess: (count) => `Successfully deleted ${count} records`,
  batchDeleteFailed: 'Batch delete failed',
  confirmDeleteTitle: 'Confirm Delete',
  confirmDeleteContent: (name) =>
    name ? `Delete "${name}"?` : 'Delete this record?',
  confirmBatchDeleteTitle: 'Confirm Batch Delete',
  confirmBatchDeleteContent: (count) => `Delete ${count} selected records?`,
};

/**
 * CRUD 列表返回值
 */
export interface UseCrudListReturn<T> {
  /** ProTable 的 request 函数 */
  request: (
    params: TableRequestParams,
    sort?: Record<string, 'ascend' | 'descend' | null>,
    filter?: TableRequestParams,
  ) => Promise<ProTableResponse<T>>;
  /** actionRef - 传给 ProTable */
  actionRef: React.MutableRefObject<TableActionRef | null>;
  /** 刷新表格 */
  reload: () => void;
  /** 导航到创建页面 */
  goCreate: () => void;
  /** 导航到编辑页面 */
  goEdit: (id: string | number) => void;
  /** 导航到详情页面 */
  goDetail: (id: string | number) => void;
  /** 删除单条记录（带确认弹窗） */
  handleDelete: (id: string | number, name?: string) => void;
  /** 批量删除（带确认弹窗） */
  handleBatchDelete: (ids: (string | number)[]) => void;
}

interface TableActionRef {
  reload?: () => void;
  reloadAndRest?: () => void;
  clearSelected?: () => void;
}

/**
 * CRUD 列表管理 Hook
 *
 * @example
 * ```tsx
 * const {
 *   request, actionRef, reload,
 *   goCreate, goEdit, goDetail,
 *   handleDelete,
 * } = useCrudList({
 *   fetchApi: getUserList,
 *   deleteApi: deleteUser,
 *   basePath: '/system/user',
 *   navigate: history.push,
 *   showMessage: (content, type) => message[type](content),
 *   showConfirm: ({ title, content, onOk }) => Modal.confirm({ title, content, onOk }),
 * });
 *
 * <ProTable
 *   actionRef={actionRef}
 *   request={request}
 *   toolBarRender={() => [
 *     <Button key="add" type="primary" onClick={goCreate}>新建</Button>
 *   ]}
 * />
 * ```
 */
export function useCrudList<T, P = Record<string, unknown>>(
  options: UseCrudListOptions<T, P>,
): UseCrudListReturn<T> {
  const { fetchApi, successCode = '200', basePath = '', navigate } = options;

  const actionRef = useRef<TableActionRef | null>(null);

  // 用 useLatestRef 稳定所有可能不稳定的回调和对象引用
  const transformParamsRef = useLatestRef(options.transformParams);
  const transformDataRef = useLatestRef(options.transformData);
  const onErrorRef = useLatestRef(options.onError);
  const deleteApiRef = useLatestRef(options.deleteApi);
  const batchDeleteApiRef = useLatestRef(options.batchDeleteApi);
  const showMessageRef = useLatestRef(options.showMessage);
  const showConfirmRef = useLatestRef(options.showConfirm);
  const msgRef = useLatestRef({
    ...DEFAULT_CRUD_MESSAGES,
    ...options.messages,
  });

  const request = useCallback(
    async (
      params: TableRequestParams,
      sort?: Record<string, 'ascend' | 'descend' | null>,
      filter?: TableRequestParams,
    ): Promise<ProTableResponse<T>> => {
      try {
        return await fetchPaginatedData<T, P>({
          params,
          sort,
          filter,
          fetchApi,
          successCode,
          transformParams: transformParamsRef.current,
          transformData: transformDataRef.current,
        });
      } catch (error) {
        onErrorRef.current?.(normalizeError(error));
        return { data: [], success: false, total: 0 };
      }
    },
    [fetchApi, successCode, onErrorRef, transformDataRef, transformParamsRef],
  );

  const reload = useCallback(() => {
    actionRef.current?.reload?.();
  }, []);

  const goCreate = useCallback(() => {
    if (basePath && navigate) {
      navigate(`${basePath}/create`);
    }
  }, [basePath, navigate]);

  const goEdit = useCallback(
    (id: string | number) => {
      if (basePath && navigate) {
        navigate(`${basePath}/edit/${id}`);
      }
    },
    [basePath, navigate],
  );

  const goDetail = useCallback(
    (id: string | number) => {
      if (basePath && navigate) {
        navigate(`${basePath}/detail/${id}`);
      }
    },
    [basePath, navigate],
  );

  const handleDelete = useCallback(
    (id: string | number, name?: string) => {
      const deleteApi = deleteApiRef.current;
      if (!deleteApi) return;

      const msg = msgRef.current;
      const doDelete = async () => {
        try {
          const response = await deleteApi(id);
          const isSuccess = isApiSuccess(response, successCode);
          if (isSuccess) {
            showMessageRef.current?.(msg.deleteSuccess, 'success');
            reload();
          } else {
            const errorMessage =
              response.msg ||
              response.message ||
              response.errorMessage ||
              msg.deleteFailed;
            showMessageRef.current?.(errorMessage, 'error');
          }
        } catch (error) {
          const err = normalizeError(error);
          showMessageRef.current?.(err.message || msg.deleteFailed, 'error');
        }
      };

      if (showConfirmRef.current) {
        showConfirmRef.current({
          title: msg.confirmDeleteTitle,
          content: msg.confirmDeleteContent(name),
          onOk: doDelete,
        });
      } else {
        doDelete();
      }
    },
    [reload, successCode, deleteApiRef, msgRef, showConfirmRef, showMessageRef],
  );

  const handleBatchDelete = useCallback(
    (ids: (string | number)[]) => {
      const batchDeleteApi = batchDeleteApiRef.current;
      if (!batchDeleteApi || ids.length === 0) return;

      const msg = msgRef.current;
      const doDelete = async () => {
        try {
          const response = await batchDeleteApi(ids);
          const isSuccess = isApiSuccess(response, successCode);
          if (isSuccess) {
            showMessageRef.current?.(
              msg.batchDeleteSuccess(ids.length),
              'success',
            );
            actionRef.current?.clearSelected?.();
            reload();
          } else {
            const errorMessage =
              response.msg ||
              response.message ||
              response.errorMessage ||
              msg.batchDeleteFailed;
            showMessageRef.current?.(errorMessage, 'error');
          }
        } catch (error) {
          const err = normalizeError(error);
          showMessageRef.current?.(
            err.message || msg.batchDeleteFailed,
            'error',
          );
        }
      };

      if (showConfirmRef.current) {
        showConfirmRef.current({
          title: msg.confirmBatchDeleteTitle,
          content: msg.confirmBatchDeleteContent(ids.length),
          onOk: doDelete,
        });
      } else {
        doDelete();
      }
    },
    [
      reload,
      successCode,
      batchDeleteApiRef,
      msgRef,
      showConfirmRef,
      showMessageRef,
    ],
  );

  return {
    request,
    actionRef,
    reload,
    goCreate,
    goEdit,
    goDetail,
    handleDelete,
    handleBatchDelete,
  };
}
